/**
 * One vocabulary for every status shown in admin, so a "Ready" video and an
 * "active" subscription never disagree about what green means.
 */
import { Tag, Tooltip } from 'antd'
import type { ReportStatus, SubscriptionStatus, TransactionStatus, VideoStatus } from '@/types'
import { useAdminTranslate } from '@/providers/i18n-provider'

type Tone = 'default' | 'processing' | 'success' | 'error' | 'warning'

/** Labels live in the catalogue under `admin.status.<kind>.<value>`. */
const VIDEO_TONES: Record<VideoStatus, Tone> = {
  none: 'default',
  uploading: 'processing',
  processing: 'processing',
  ready: 'success',
  failed: 'error',
}

const SUBSCRIPTION_TONES: Record<SubscriptionStatus, Tone> = {
  active: 'success',
  expired: 'default',
  revoked: 'error',
}

const TRANSACTION_TONES: Record<TransactionStatus, Tone> = {
  pending: 'warning',
  paid: 'success',
  failed: 'error',
  refunded: 'default',
}

const REPORT_TONES: Record<ReportStatus, Tone> = {
  open: 'warning',
  dismissed: 'default',
  actioned: 'success',
}

export type StatusKind = 'video' | 'subscription' | 'transaction' | 'report' | 'published'

export interface StatusTagProps {
  kind: StatusKind
  value: string | boolean
  /** Failure reason, shown on hover (US-A04 AC5). */
  reason?: string | null
}

export const StatusTag = ({ kind, value, reason }: StatusTagProps) => {
  const translate = useAdminTranslate()
  let descriptor: { tone: Tone; label: string }

  const from = <T extends string>(tones: Record<T, Tone>, group: string, fallback: T) => {
    const key = (value as T) in tones ? (value as T) : fallback
    return { tone: tones[key], label: translate(`admin.status.${group}.${key}`) }
  }

  switch (kind) {
    case 'video':
      descriptor = from(VIDEO_TONES, 'video', 'none')
      break
    case 'subscription':
      descriptor = from(SUBSCRIPTION_TONES, 'subscription', 'expired')
      break
    case 'transaction':
      descriptor = from(TRANSACTION_TONES, 'transaction', 'pending')
      break
    case 'report':
      descriptor = from(REPORT_TONES, 'report', 'open')
      break
    case 'published':
    default:
      descriptor = value
        ? { tone: 'success', label: translate('admin.status.published') }
        : { tone: 'default', label: translate('admin.status.draft') }
      break
  }

  const tag = <Tag color={descriptor.tone}>{descriptor.label}</Tag>
  return reason ? <Tooltip title={reason}>{tag}</Tooltip> : tag
}
